import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { testsApi, Test } from '@/api/tests';
import { Book } from '@/components/icons';
import {
    CardGrid,
    EmptyState,
    FACET_ALL_VALUE,
    FacetSelect,
    SearchField,
} from '@/components/system';
import { Button } from '@/components/ui/button';
import { useModalContext } from '@/components/ui/modal-provider';
import { useDebounce } from '@/hooks/use-debounce';
import { buildCategoryOptions, filterAndSortInstruments } from '@/lib/instrument-filters';
import WrapperLayout from '@/pages/components/WrapperLayout';
import { pageVariants, containerVariants, itemVariants } from '../dashboard/animations';
import SendTestDialog from '../tests/components/SendTestDialog';
import InstrumentCard, { InstrumentCardSkeleton } from './components/InstrumentCard';
import { TestDetailSheet } from './components/TestDetailSheet';

const MotionCardGrid = motion(CardGrid);

const SKELETON_COUNT = 8;

export default function AssetsPage() {
    const { openWizardModal } = useModalContext();
    const navigate = useNavigate();
    const [selectedTest, setSelectedTest] = useState<Test | null>(null);
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [category, setCategory] = useState(FACET_ALL_VALUE);
    const debouncedQuery = useDebounce(searchQuery, 250);

    const { data: tests = [], isLoading } = useQuery({
        queryKey: ['tests', 'list'],
        queryFn: testsApi.list,
    });

    const categoryOptions = useMemo(() => buildCategoryOptions(tests), [tests]);

    const filteredTests = useMemo(
        () => filterAndSortInstruments(tests, { query: debouncedQuery, category }),
        [tests, category, debouncedQuery],
    );

    const hasActiveFilters = searchQuery.trim() !== '' || category !== FACET_ALL_VALUE;

    const handleClearFilters = () => {
        setSearchQuery('');
        setCategory(FACET_ALL_VALUE);
    };

    const handleSendClick = (test: Test) => {
        openWizardModal({
            content: <SendTestDialog test={test} />,
            size: 'md',
        });
    };

    const handleViewDetails = (test: Test) => {
        setSelectedTest(test);
        setIsSheetOpen(true);
    };

    const handlePreviewClick = (test: Test) => {
        navigate(`/assets/${test.id}/preview`);
    };

    const handleAssignFromSheet = (test: Test) => {
        setIsSheetOpen(false);
        handleSendClick(test);
    };

    return (
        <WrapperLayout
            title="Instrumentos"
            leftIcon={Book}
            meta={
                isLoading ? undefined : (
                    <span aria-live="polite">
                        {hasActiveFilters
                            ? `${filteredTests.length} de ${tests.length}`
                            : `${tests.length} disponíveis`}
                    </span>
                )
            }
            toolbar={{
                search: (
                    <SearchField
                        className="w-full"
                        placeholder="Buscar por nome, sigla ou tema..."
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                    />
                ),
                filters:
                    categoryOptions.length > 0 ? (
                        <FacetSelect
                            label="Categoria"
                            options={categoryOptions}
                            totalCount={tests.length}
                            value={category}
                            onChange={setCategory}
                            searchPlaceholder="Filtrar categoria..."
                        />
                    ) : undefined,
            }}
        >
            <motion.div
                className="space-y-8"
                variants={pageVariants}
                initial="hidden"
                animate="visible"
            >
                <MotionCardGrid
                    columns={3}
                    gap="default"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    {isLoading ? (
                        Array.from({ length: SKELETON_COUNT }, (_, index) => (
                            <InstrumentCardSkeleton key={index} />
                        ))
                    ) : filteredTests.length > 0 ? (
                        filteredTests.map((test) => (
                            <motion.div key={test.id} variants={itemVariants} className="h-full">
                                <InstrumentCard
                                    test={test}
                                    onOpen={handleViewDetails}
                                    onAssign={handleSendClick}
                                    onPreview={handlePreviewClick}
                                />
                            </motion.div>
                        ))
                    ) : (
                        <div className="col-span-full py-6">
                            <EmptyState
                                icon={Book}
                                title="Nenhum instrumento encontrado"
                                description="Ajuste sua busca ou o filtro de categoria para localizar um instrumento disponível."
                                action={
                                    hasActiveFilters ? (
                                        <Button variant="outline" onClick={handleClearFilters}>
                                            Limpar filtros
                                        </Button>
                                    ) : undefined
                                }
                            />
                        </div>
                    )}
                </MotionCardGrid>

                <TestDetailSheet
                    test={selectedTest}
                    isOpen={isSheetOpen}
                    onClose={() => setIsSheetOpen(false)}
                    onAssign={handleAssignFromSheet}
                    onPreview={handlePreviewClick}
                />
            </motion.div>
        </WrapperLayout>
    );
}
